import clsx from "clsx"
import { useTranslation } from "react-i18next"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { adjustmentsHistoryAtom, setHistoryIndex } from "@/store/store-history"
import type { AdjustmentsHistoryRecord } from "@/lib/catalog/types"
import { getHistoryChangedValueDisplay, translateHistoryMessage } from "@/i18n/translate-history"

import { useEffect, useRef, useState } from "react"
import { Spinner } from "./ui/Spinner"
import { expandedPanelsAtom } from "@/store/ui-views-state"
import { currentPhotoAtom } from "@/store/photo/photos-state"
import { photoLoadingStateAtom } from "@/store/editor/photo-loading"
import { currentHoverPreviewAtom } from "@/store/editor/photo-preview"
import { ContentTooltipWrapper } from "./ui/ContentTooltip"

const HistoryChangedValue = ({ changedValue }: { changedValue?: string }) => {
	const ref = useRef<HTMLDivElement>(null)
	const [isTruncated, setIsTruncated] = useState(false)

	useEffect(() => {
		const el = ref.current
		if (el) {
			setIsTruncated(el.scrollWidth > el.clientWidth)
		}
	}, [changedValue])

	return (
		<div className="min-w-0 flex-1 text-right">
			<ContentTooltipWrapper tooltip={changedValue || undefined} allowOpen={isTruncated}>
				<div ref={ref} className="truncate">
					{changedValue}
				</div>
			</ContentTooltipWrapper>
		</div>
	)
}

export const HistoryPanel = () => {
	const { t } = useTranslation("components")
	const [currentPhoto] = useAtom(currentPhotoAtom)
	const isExpanded = useAtomValue(expandedPanelsAtom)["history"]
	const [history] = useAtom(adjustmentsHistoryAtom)
	const scrollRef = useRef<HTMLDivElement | null>(null)
	const [loadingState] = useAtom(photoLoadingStateAtom)
	const setCurrentHoverPreview = useSetAtom(currentHoverPreviewAtom)

	const getHistoryMessage = (record: AdjustmentsHistoryRecord) => {
		return translateHistoryMessage(record, t)
	}

	useEffect(() => {
		if (!isExpanded) {
			return
		}

		scrollRef.current?.scrollTo({ top: -scrollRef.current?.scrollHeight })
	}, [history, isExpanded])

	return (
		<div aria-label={t("historyPanel.ariaHistoryPanel")}>
			{!currentPhoto && (
				<div className="w-full flex-none px-1 pt-2 text-center text-sm text-base-400">
					{t("historyPanel.selectPhotoPrompt")}
				</div>
			)}
			{currentPhoto && history.length == 0 && (
				<div className="w-full flex-none px-1 pt-2 text-center text-sm text-base-400">
					{t("historyPanel.noHistory")}
				</div>
			)}
			<div
				ref={scrollRef}
				className="flex w-full flex-col-reverse"
				onMouseLeave={() => {
					setCurrentHoverPreview(null)
				}}
			>
				{currentPhoto &&
					history.length > 0 &&
					history
						.filter((record) => typeof record !== "undefined")
						.map((record) => {
							const message = getHistoryMessage(record)
							const changedValue = getHistoryChangedValueDisplay(record, t)

							return (
								<div
									key={record.stepIndex}
									role="button"
									tabIndex={0}
									aria-label={`${message}${changedValue ? `: ${changedValue}` : ""}`}
									aria-current={
										record.stepIndex == currentPhoto?.historyIndex &&
										record.photoId == currentPhoto?.id
											? "step"
											: undefined
									}
									className={clsx(
										record.stepIndex == currentPhoto?.historyIndex &&
											record.photoId == currentPhoto?.id
											? "bg-base-300 text-base-800"
											: "bg-transparent text-base-300 hover:bg-base-700 hover:text-base-300",
										"my-0.5 flex w-full flex-row items-center gap-9 self-start rounded-sm px-2 py-1 text-left text-xs",
									)}
									onClick={(e) => {
										if (currentPhoto) {
											setHistoryIndex(record.stepIndex, e.target)
										}
									}}
									onMouseEnter={() => {
										setCurrentHoverPreview({ ...record, type: "history" })
									}}
								>
									<div>{message}</div>
									<HistoryChangedValue changedValue={changedValue} />
								</div>
							)
						})}
			</div>
			{loadingState == "loading" && (
				<div className="absolute z-10 flex size-full items-center justify-center bg-base-800">
					<Spinner size={24} />
				</div>
			)}
		</div>
	)
}
